import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAppSelector } from '../../redux/store/hooks';
import { useDiagnosticRouting, useUpdateRoutingStatus } from '../../services/httpServices/queries';
import { joinFullName } from '../../utils/patientName';
import { attachmentService, Attachment } from '../../services/httpServices/attachmentService';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Activity,
  User,
  Hash,
  Calendar,
  FlaskConical,
  CheckCircle,
  ArrowRight,
  X,
  Upload,
  FileText,
  Image,
  Download,
  Trash2,
  StickyNote,
  Send,
  Clock,
  Package,
  ChevronRight,
} from 'lucide-react';

const statusColors: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  WAITING: 'bg-sky-50 text-sky-700 border-sky-200',
  IN_PROGRESS: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  CANCELLED: 'bg-rose-50 text-rose-700 border-rose-200',
};

const statusIconColors: Record<string, string> = {
  PENDING: 'text-amber-600',
  WAITING: 'text-sky-600',
  IN_PROGRESS: 'text-indigo-600',
  COMPLETED: 'text-emerald-600',
  CANCELLED: 'text-rose-600',
};

const nextStatusMap: Record<string, string> = {
  PENDING: 'WAITING',
  WAITING: 'IN_PROGRESS',
  IN_PROGRESS: 'COMPLETED',
};

const nextStatusLabels: Record<string, string> = {
  WAITING: 'Start Processing',
  IN_PROGRESS: 'Mark Completed',
  COMPLETED: 'Complete',
};

/* ──────────────── helpers ──────────────── */

function formatDateTime(d: Date) {
  return d.toLocaleString('en-GB', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

interface ParsedNote {
  id: number;
  timestamp: string;
  author: string;
  text: string;
}

function parseNotes(raw?: string): ParsedNote[] {
  if (!raw || !raw.trim()) return [];
  const lines = raw.split('\n').filter((l) => l.trim());
  const out: ParsedNote[] = [];
  let id = 0;
  for (const line of lines) {
    const m = line.match(/^\[(.+?)\]\s*(?:\((.+?)\)\s*)?(.+)$/);
    if (m) {
      out.push({ id: ++id, timestamp: m[1].trim(), author: m[2]?.trim() || 'System', text: m[3].trim() });
    } else {
      out.push({ id: ++id, timestamp: '', author: '', text: line.trim() });
    }
  }
  return out.reverse(); // newest first
}

function appendNote(raw: string | undefined, author: string, text: string) {
  const entry = `[${formatDateTime(new Date())}] (${author}) ${text.trim()}`;
  return raw ? `${raw}\n${entry}` : entry;
}

/* ──────────────── sub-components ──────────────── */

function ItemAttachmentSection({
  itemId,
  onCountChange,
}: {
  itemId: string;
  onCountChange?: (itemId: string, count: number) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Attachment | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await attachmentService.getAttachments('diagnostic_receipt_item', itemId);
      const list = res.data || [];
      setAttachments(list);
      onCountChange?.(itemId, list.length);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [itemId, onCountChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      await attachmentService.uploadFile(file, 'diagnostic_receipt_item', itemId, description || undefined);
      setFile(null);
      setDescription('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 2000);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      await attachmentService.deleteAttachment(deleteTarget.id);
      await load();
      setDeleteTarget(null);
    } catch (e) {
      console.error(e);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownload = async (id: string) => {
    setDownloadingId(id);
    try {
      const res = await attachmentService.getDownloadUrl(id);
      if (res.data?.downloadUrl) {
        window.open(res.data.downloadUrl, '_blank');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-3 pt-4 border-t border-border/60">
      <h4 className="text-[11px] font-semibold text-text-muted uppercase tracking-wider flex items-center gap-1.5">
        <FileText size={12} />
        Test Documents & Data
      </h4>

      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="hidden"
          id={`item-upload-${itemId}`}
        />
        <label
          htmlFor={`item-upload-${itemId}`}
          className="flex-1 flex items-center gap-2 px-3 py-2 border border-border rounded-lg bg-surface text-text text-xs cursor-pointer hover:border-primary transition-colors"
        >
          <FileText size={14} className="text-text-muted" />
          <span className="truncate">{file ? file.name : 'Choose file...'}</span>
        </label>
        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-xs font-medium flex items-center gap-1 disabled:opacity-50"
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          Upload
        </button>
      </div>

      <div className="space-y-1.5">
        <label className="block text-xs font-medium text-text">Description / Notes</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-border rounded-lg bg-surface text-text text-xs focus:outline-none focus:ring-1 focus:ring-primary resize-none"
          placeholder="Notes about this file..."
        />
      </div>

      {uploadSuccess && (
        <div className="p-2 bg-success/10 text-success rounded-lg text-xs flex items-center gap-1.5">
          <CheckCircle size={12} />
          Uploaded successfully!
        </div>
      )}

      <div className="space-y-2">
        {loading && attachments.length === 0 && (
          <div className="flex items-center gap-2 py-2 text-text-muted">
            <Loader2 size={14} className="animate-spin" />
            <span className="text-xs">Loading attachments...</span>
          </div>
        )}
        {attachments.length === 0 && !loading && (
          <p className="text-xs text-text-muted py-1">No attachments yet for this test</p>
        )}
        {attachments.map((att) => {
          const isImage = att.fileType?.startsWith('image/');
          return (
            <div
              key={att.id}
              className="flex items-center gap-3 p-2.5 bg-surface rounded-lg border border-border hover:border-primary/30 transition-colors"
            >
              <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                {isImage ? (
                  <Image size={14} className="text-primary" />
                ) : (
                  <FileText size={14} className="text-primary" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-text truncate">{att.fileName}</p>
                <p className="text-[10px] text-text-muted">
                  {(att.fileSize / 1024 / 1024).toFixed(2)} MB
                  {att.description && ` · ${att.description}`}
                </p>
              </div>
              <button
                onClick={() => handleDownload(att.id)}
                disabled={downloadingId === att.id}
                className="p-1.5 hover:bg-primary/10 rounded transition-colors disabled:opacity-50"
                title="Download"
              >
                {downloadingId === att.id ? (
                  <Loader2 size={12} className="animate-spin text-primary" />
                ) : (
                  <Download size={12} className="text-primary" />
                )}
              </button>
              <button
                onClick={() => setDeleteTarget(att)}
                disabled={deletingId === att.id}
                className="p-1.5 hover:bg-danger/10 rounded transition-colors disabled:opacity-50"
                title="Delete"
              >
                {deletingId === att.id ? (
                  <Loader2 size={12} className="animate-spin text-danger" />
                ) : (
                  <Trash2 size={12} className="text-danger" />
                )}
              </button>
            </div>
          );
        })}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete attachment?"
        description={
          <>
            This will permanently delete{' '}
            <strong className="text-text">{deleteTarget?.fileName}</strong>. This cannot be undone.
          </>
        }
        confirmLabel="Delete attachment"
        loading={!!deleteTarget && deletingId === deleteTarget.id}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

/* ──────────────── main page ──────────────── */

export function DiagnosticRoutingViewEdit() {
  const { routingId } = useParams<{ routingId: string }>();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);

  const { data, isLoading, error } = useDiagnosticRouting(routingId);
  const updateMutation = useUpdateRoutingStatus();

  const [newNoteText, setNewNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [attachmentCounts, setAttachmentCounts] = useState<Record<string, number>>({});

  const handleAttachmentCountChange = useCallback((itemId: string, count: number) => {
    setAttachmentCounts((prev) => (prev[itemId] === count ? prev : { ...prev, [itemId]: count }));
  }, []);

  const routing = data?.data;
  const receipt = routing?.receipt;
  const department = routing?.department;
  const items = useMemo(
    () =>
      receipt?.items?.filter((item: any) => item.diagnosticDepartmentId === routing?.diagnosticDepartmentId) || [],
    [receipt, routing],
  );
  const nextStatus = routing?.status ? nextStatusMap[routing.status] : undefined;

  const docsLoaded = items.every((item: any) => attachmentCounts[item.id] !== undefined);
  const missingDocItems = items.filter((item: any) => (attachmentCounts[item.id] ?? 0) === 0);
  const requiresDocs = nextStatus === 'COMPLETED';
  const canComplete = !requiresDocs || (docsLoaded && missingDocItems.length === 0);

  const existingNotes = useMemo(() => parseNotes(routing?.departmentNotes), [routing?.departmentNotes]);

  const authorName = user?.name || user?.username || 'Staff';

  const handleAddNote = async () => {
    if (!routingId || !newNoteText.trim() || !routing) return;
    setAddingNote(true);
    try {
      const updated = appendNote(routing.departmentNotes, authorName, newNoteText);
      await updateMutation.mutateAsync({
        routingId,
        data: { status: routing.status, departmentNotes: updated },
      });
      setNewNoteText('');
    } catch (e) {
      console.error(e);
    } finally {
      setAddingNote(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!routingId || !nextStatus) return;
    try {
      await updateMutation.mutateAsync({
        routingId,
        data: {
          status: nextStatus,
          departmentNotes: routing?.departmentNotes,
        },
      });
      toast.success(`Status updated to ${nextStatus.replace(/_/g, ' ')}`);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update status');
    }
  };

  const handleCancel = async () => {
    if (!routingId) return;
    await updateMutation.mutateAsync({
      routingId,
      data: {
        status: 'CANCELLED',
        departmentNotes: routing?.departmentNotes,
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !routing) {
    return (
      <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-xl text-danger max-w-xl mx-auto mt-10">
        <AlertCircle size={18} />
        <span>Failed to load routing details. Please try again.</span>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 pb-10">
      {/* Top Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-text-muted hover:text-text transition-colors text-sm font-medium cursor-pointer"
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <span className="text-[11px] text-text-muted font-mono bg-surface px-2 py-1 rounded-md border border-border">
          Routing ID: {routing.id.slice(0, 8)}
        </span>
      </div>

      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-primary/10 rounded-xl">
          <Activity className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-text tracking-tight">{department?.name || 'Diagnostic Order'}</h1>
          <p className="text-sm text-text-muted mt-0.5 flex items-center gap-2">
            <span>Receipt {receipt?.receiptNumber}</span>
            <span className="text-border">·</span>
            <span>{routing.roomNumber || 'N/A'}</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ─── Left Column ─── */}
        <div className="lg:col-span-2 space-y-6">
          {/* Patient & Receipt Card */}
          <div className="bg-surface border border-border rounded-2xl p-5 shadow-sm">
            <h2 className="text-xs font-semibold text-text uppercase tracking-wider mb-4 flex items-center gap-2">
              <User size={14} className="text-text-muted" />
              Patient & Receipt
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 bg-background rounded-xl p-3.5 border border-border/70">
                <div className="p-2.5 bg-primary/10 rounded-lg">
                  <User size={16} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-text-muted uppercase tracking-wide">Patient</p>
                  <p className="text-sm font-semibold text-text truncate">
                    {joinFullName(receipt?.patient || {})}
                  </p>
                  <p className="text-[11px] text-text-muted">{receipt?.patient?.mobile}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-background rounded-xl p-3.5 border border-border/70">
                <div className="p-2.5 bg-primary/10 rounded-lg">
                  <Hash size={16} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-text-muted uppercase tracking-wide">Receipt No</p>
                  <p className="text-sm font-semibold text-text font-mono truncate">{receipt?.receiptNumber}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-background rounded-xl p-3.5 border border-border/70">
                <div className="p-2.5 bg-primary/10 rounded-lg">
                  <Calendar size={16} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-text-muted uppercase tracking-wide">Created</p>
                  <p className="text-sm font-semibold text-text">
                    {receipt?.createdAt ? new Date(receipt.createdAt).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-background rounded-xl p-3.5 border border-border/70">
                <div className="p-2.5 bg-primary/10 rounded-lg">
                  <FlaskConical size={16} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-text-muted uppercase tracking-wide">Tests</p>
                  <p className="text-sm font-semibold text-text">{items.length} item(s)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tests List */}
          <div className="bg-surface border border-border rounded-2xl p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-primary/10 rounded-md">
                <FlaskConical size={16} className="text-primary" />
              </div>
              <h2 className="text-base font-semibold text-text">Department Tests</h2>
              <span className="ml-auto text-xs font-medium text-text-muted bg-background px-2 py-0.5 rounded-full border border-border">
                {items.length}
              </span>
            </div>

            {items.length === 0 ? (
              <div className="py-8 text-center">
                <Package size={32} className="text-text-muted/40 mx-auto mb-2" />
                <p className="text-sm text-text-muted">No tests assigned to this department for this receipt.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((item: any) => (
                  <div
                    key={item.id}
                    className="p-4 bg-background rounded-xl border border-border/70 space-y-3 hover:border-primary/20 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                            item.status === 'COMPLETED'
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : item.status === 'REFUNDED'
                              ? 'bg-rose-50 text-rose-700 border-rose-200'
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                          }`}
                        >
                          {item.status}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-text truncate">{item.testName}</p>
                          <p className="text-[11px] text-text-muted">
                            ৳{Number(item.unitPrice).toLocaleString()} × {item.quantity} ={' '}
                            <span className="font-medium text-text">৳{Number(item.totalPrice).toLocaleString()}</span>
                          </p>
                        </div>
                      </div>
                      {item.orderBarcode && (
                        <p className="text-[11px] font-mono text-text-muted bg-surface px-2 py-1 rounded-md border border-border shrink-0">
                          {item.orderBarcode}
                        </p>
                      )}
                    </div>
                    <ItemAttachmentSection itemId={item.id} onCountChange={handleAttachmentCountChange} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ─── Right Column (Sidebar) ─── */}
        <div className="space-y-6">
          {/* Status Card */}
          <div className="bg-surface border border-border rounded-2xl p-5 shadow-sm space-y-4">
            <h2 className="text-xs font-semibold text-text uppercase tracking-wider flex items-center gap-2">
              <CheckCircle size={14} className="text-text-muted" />
              Status Control
            </h2>

            <div className="flex items-center gap-3">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${
                  statusColors[routing.status] || 'bg-surface text-text-muted border-border'
                }`}
              >
                {routing.status === 'COMPLETED' && <CheckCircle size={12} />}
                {routing.status === 'CANCELLED' && <X size={12} />}
                {routing.status === 'IN_PROGRESS' && <Loader2 size={12} className="animate-spin" />}
                {routing.status.replace(/_/g, ' ')}
              </span>
            </div>

            {nextStatus && (
              <div className="flex items-center gap-2 text-xs text-text-muted">
                <ChevronRight size={14} className={statusIconColors[nextStatus] || 'text-text-muted'} />
                <span>Next step: {nextStatus.replace(/_/g, ' ')}</span>
              </div>
            )}

            <div className="flex flex-col gap-2 pt-2">
              {nextStatus && (
                <>
                  {requiresDocs && docsLoaded && missingDocItems.length > 0 && (
                    <div className="p-2.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-xs flex items-start gap-1.5">
                      <AlertCircle size={13} className="mt-0.5 shrink-0" />
                      <span>
                        Test Documents &amp; Data are required before completing. Upload at least one file for:{' '}
                        <span className="font-medium">
                          {missingDocItems.map((item: any) => item.testName).join(', ')}
                        </span>
                      </span>
                    </div>
                  )}
                  <button
                    onClick={handleStatusUpdate}
                    disabled={updateMutation.isPending || !canComplete}
                    title={!canComplete ? 'Upload Test Documents & Data for all tests to complete' : undefined}
                    className="w-full px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 shadow-sm"
                  >
                    {updateMutation.isPending ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <ArrowRight size={14} />
                    )}
                    {nextStatusLabels[nextStatus] || `Mark ${nextStatus.replace(/_/g, ' ')}`}
                  </button>
                </>
              )}
              {routing.status !== 'CANCELLED' && routing.status !== 'COMPLETED' && (
                <button
                  onClick={handleCancel}
                  disabled={updateMutation.isPending}
                  className="w-full px-4 py-2.5 border border-danger/30 text-danger rounded-xl hover:bg-danger/5 transition-colors text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <X size={14} />
                  Cancel Order
                </button>
              )}
            </div>
          </div>

          {/* Notes Card */}
          <div className="bg-surface border border-border rounded-2xl p-5 shadow-sm space-y-4">
            <h2 className="text-xs font-semibold text-text uppercase tracking-wider flex items-center gap-2">
              <StickyNote size={14} className="text-text-muted" />
              Department Notes
            </h2>

            {/* Existing Notes Timeline */}
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
              {existingNotes.length === 0 ? (
                <div className="py-6 text-center">
                  <StickyNote size={28} className="text-text-muted/30 mx-auto mb-2" />
                  <p className="text-xs text-text-muted">No notes yet. Add one below.</p>
                </div>
              ) : (
                existingNotes.map((note) => (
                  <div key={note.id} className="relative pl-4 border-l-2 border-primary/20">
                    <div className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-primary/40" />
                    <div className="bg-background rounded-xl p-3 border border-border/60 space-y-1">
                      <p className="text-sm text-text leading-relaxed whitespace-pre-wrap">{note.text}</p>
                      <div className="flex items-center gap-2 text-[10px] text-text-muted">
                        {note.timestamp && (
                          <span className="flex items-center gap-1">
                            <Clock size={10} />
                            {note.timestamp}
                          </span>
                        )}
                        {note.author && <span>by {note.author}</span>}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Add Note */}
            <div className="space-y-2 pt-2 border-t border-border/60">
              <label className="block text-xs font-medium text-text">Add a note</label>
              <textarea
                value={newNoteText}
                onChange={(e) => setNewNoteText(e.target.value)}
                rows={3}
                className="w-full px-3 py-2.5 border border-border rounded-xl bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none transition-shadow"
                placeholder="Write a new note…"
              />
              <button
                onClick={handleAddNote}
                disabled={!newNoteText.trim() || addingNote}
                className="w-full px-4 py-2 bg-primary/10 text-primary rounded-xl hover:bg-primary/20 transition-colors text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-40"
              >
                {addingNote ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                Add Note
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
